import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Search, Link2, AlertTriangle, Check } from 'lucide-react';
import { toast } from 'sonner';
import { formatCurrency } from '../../lib/formatters';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LinkDealDialog = ({ open, onOpenChange, contact, getAuthHeaders, onSuccess }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [linking, setLinking] = useState(false);
  const [selectedDeal, setSelectedDeal] = useState(null);
  const [showOrphansOnly, setShowOrphansOnly] = useState(true);

  // Fetch deals based on search
  const fetchDeals = useCallback(async () => {
    if (!open) return;
    
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: '50' });
      if (searchQuery.trim()) {
        params.append('search', searchQuery.trim());
      }
      if (showOrphansOnly) {
        params.append('orphans', 'true');
      }
      
      const response = await axios.get(`${API}/deals?${params.toString()}`, { 
        headers: getAuthHeaders() 
      });
      setDeals(response.data);
    } catch (error) {
      console.error('Failed to fetch deals:', error);
      toast.error('Failed to load deals');
    } finally {
      setLoading(false);
    }
  }, [open, searchQuery, showOrphansOnly, getAuthHeaders]);

  useEffect(() => {
    const debounce = setTimeout(() => {
      fetchDeals();
    }, 300);
    return () => clearTimeout(debounce);
  }, [fetchDeals]);

  // Reset state when dialog closes
  useEffect(() => {
    if (!open) {
      setSearchQuery('');
      setSelectedDeal(null);
    }
  }, [open]);

  const handleLinkDeal = async () => {
    if (!selectedDeal || !contact) return;
    
    setLinking(true);
    try {
      // Use PATCH to update only the company_id field
      await axios.patch(`${API}/deals/${selectedDeal.id}`, {
        company_id: contact.id
      }, { headers: getAuthHeaders() });
      
      toast.success(`Deal #${selectedDeal.deal_id} linked to ${contact.company_name}`);
      onOpenChange(false);
      if (onSuccess) onSuccess();
    } catch (error) {
      console.error('Failed to link deal:', error);
      toast.error(error.response?.data?.detail || 'Failed to link deal');
    } finally {
      setLinking(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Link2 className="h-5 w-5 text-blue-600" />
            Link Deal to {contact?.company_name}
          </DialogTitle>
          <DialogDescription>
            Search for an existing deal to link to this merchant contact.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 flex-1 overflow-hidden flex flex-col">
          {/* Search and Filter */}
          <div className="flex gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                type="text"
                placeholder="Search by deal ID, name, or shortcode..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                data-testid="link-deal-search"
              />
            </div>
            <Button
              variant={showOrphansOnly ? "default" : "outline"}
              onClick={() => setShowOrphansOnly(!showOrphansOnly)}
              className={showOrphansOnly ? "bg-orange-600 hover:bg-orange-700" : ""}
              data-testid="orphans-only-toggle"
            >
              <AlertTriangle className="h-4 w-4 mr-2" />
              Orphans Only
            </Button>
          </div>

          {/* Info Banner */}
          {showOrphansOnly && (
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 flex items-center gap-2 text-sm">
              <AlertTriangle className="h-4 w-4 text-orange-600 flex-shrink-0" />
              <span className="text-orange-700">
                Showing only deals without a merchant contact. Toggle off to see all deals.
              </span>
            </div>
          )}

          {/* Deals List */}
          <div className="flex-1 overflow-y-auto border rounded-lg">
            {loading ? (
              <div className="p-8 text-center text-gray-500">Loading deals...</div>
            ) : deals.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                {showOrphansOnly 
                  ? "No orphan deals found. All deals have merchant contacts linked."
                  : "No deals found matching your search."}
              </div>
            ) : (
              <div className="divide-y">
                {deals.map((deal) => (
                  <div
                    key={deal.id}
                    className={`p-3 cursor-pointer transition-colors ${
                      selectedDeal?.id === deal.id 
                        ? 'bg-blue-50 border-l-4 border-blue-600' 
                        : 'hover:bg-gray-50'
                    }`}
                    onClick={() => setSelectedDeal(deal)}
                    data-testid={`deal-option-${deal.deal_id}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        {selectedDeal?.id === deal.id && (
                          <Check className="h-5 w-5 text-blue-600" />
                        )}
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-mono font-medium text-blue-600">
                              #{deal.deal_id}
                            </span>
                            <span className="font-medium">{deal.deal_name}</span>
                            {deal.deal_shortcode && (
                              <Badge variant="outline" className="text-xs">
                                {deal.deal_shortcode}
                              </Badge>
                            )}
                          </div>
                          <div className="text-sm text-gray-500 flex items-center gap-3 mt-1">
                            <span>Funding: {formatCurrency(deal.funding_amount)}</span>
                            <span>•</span>
                            <span>Status: {deal.status}</span>
                            {deal.company_name && (
                              <>
                                <span>•</span>
                                <span className="text-orange-600">
                                  Currently: {deal.company_name}
                                </span>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleLinkDeal}
              disabled={!selectedDeal || linking}
              data-testid="confirm-link-deal"
            >
              {linking ? 'Linking...' : `Link Deal #${selectedDeal?.deal_id || '...'}`}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default LinkDealDialog;
